// WS5 PR4a — Touch controls per drag-rotate + pinch-FOV (mobile FP).
//
// Convivenza con joystick:
//   - `excludeRect()` viene chiamato A TOUCHSTART (cache per touch attivo
//     come da Codex Q4) e ogni nuovo touchstart su area joystick e' SCARTATO.
//   - Touch identifier tracking: ricordiamo i `Touch.identifier` di drag
//     attivo / pinch attivi; touch successivi diventano pinch solo se ce
//     n'e' uno solo gia' attivo.
//
// Sensitivity drag-rotate: 0.006 rad/px (3× MOUSE_SENSITIVITY desktop).
// FOV pinch: usa snapshot startDist + startFov (pattern non-incrementale)
// per evitare double-apply quando `currentFov` cambia mid-pinch.

export const TOUCH_ROTATE_SENSITIVITY = 0.006; // rad/px
export const FOV_MIN_DEG = 45;
export const FOV_MAX_DEG = 65;
// Pinch-zoom: scaleRatio = currentDist / startDist. ratio>1 (dita si
// allargano) → zoom-in → FOV diminuisce. Mappiamo scaleRatio [0.5, 2.0]
// linearmente a fov delta ∈ [+15, -15] deg attorno a startFov.
const PINCH_FOV_GAIN = 20; // moltiplica (1 - scaleRatio) → delta deg

export interface TouchRotateDelta {
  readonly deltaYaw: number;
  readonly deltaPitch: number;
}

export interface TouchPinchUpdate {
  readonly fovDeg: number;
}

export interface TouchControlsCallbacks {
  // Yaw/pitch delta in radianti.
  readonly onRotate: (delta: TouchRotateDelta) => void;
  // Aggiornamento FOV assoluto (gia' clamp [FOV_MIN_DEG, FOV_MAX_DEG]).
  readonly onPinch: (update: TouchPinchUpdate) => void;
}

export interface TouchControlsOptions {
  // Snapshot FOV corrente al touchstart pinch (per ancoraggio non incrementale).
  readonly getCurrentFovDeg: () => number;
  // Rect dell'area joystick da escludere ai nuovi touch (cache una volta a
  // touchstart). null → nessuna esclusione.
  readonly getJoystickRect?: () => DOMRect | null;
  readonly sensitivity?: number;
  readonly fovMinDeg?: number;
  readonly fovMaxDeg?: number;
}

export interface TouchControls {
  dispose(): void;
}

// Stato drag (1 dito): identifier + ultima posizione conosciuta.
interface DragState {
  identifier: number;
  lastX: number;
  lastY: number;
}

// Stato pinch (2 dita): identifier coppia + distanza iniziale + fov al
// momento dello start (NON aggiornato mid-pinch).
interface PinchState {
  idA: number;
  idB: number;
  startDist: number;
  startFovDeg: number;
}

export function createTouchControls(
  canvas: HTMLElement,
  callbacks: TouchControlsCallbacks,
  options: TouchControlsOptions,
): TouchControls {
  const sensitivity = options.sensitivity ?? TOUCH_ROTATE_SENSITIVITY;
  const fovMin = options.fovMinDeg ?? FOV_MIN_DEG;
  const fovMax = options.fovMaxDeg ?? FOV_MAX_DEG;

  let drag: DragState | null = null;
  let pinch: PinchState | null = null;

  const isInJoystick = (x: number, y: number): boolean => {
    const rect = options.getJoystickRect?.() ?? null;
    if (rect === null) return false;
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
  };

  const onTouchStart = (ev: TouchEvent): void => {
    // Scan dei changedTouches: per ognuno, se NON e' su area joystick e
    // non abbiamo gia' un drag → diventa drag. Se abbiamo gia' un drag e
    // arriva un secondo touch fuori joystick → upgrade a pinch.
    for (let i = 0; i < ev.changedTouches.length; i++) {
      const t = ev.changedTouches.item(i);
      if (t === null) continue;
      if (isInJoystick(t.clientX, t.clientY)) continue;
      if (drag === null && pinch === null) {
        drag = { identifier: t.identifier, lastX: t.clientX, lastY: t.clientY };
        ev.preventDefault();
        continue;
      }
      if (drag !== null && pinch === null && t.identifier !== drag.identifier) {
        // Upgrade a pinch: archivia distanza iniziale + fov.
        // Recuperiamo le coordinate del primo dito dal drag state (lastX/Y).
        const dx = t.clientX - drag.lastX;
        const dy = t.clientY - drag.lastY;
        const startDist = Math.hypot(dx, dy);
        if (startDist > 0) {
          pinch = {
            idA: drag.identifier,
            idB: t.identifier,
            startDist,
            startFovDeg: options.getCurrentFovDeg(),
          };
          ev.preventDefault();
        }
      }
    }
  };

  const onTouchMove = (ev: TouchEvent): void => {
    if (drag === null && pinch === null) return;
    // Pinch ha priorita': se entrambi i suoi due dita sono in ev.touches
    // applichiamo la pinch ed evitiamo di calcolare drag deltas.
    if (pinch !== null) {
      let tA: Touch | null = null;
      let tB: Touch | null = null;
      for (let i = 0; i < ev.touches.length; i++) {
        const t = ev.touches.item(i);
        if (t === null) continue;
        if (t.identifier === pinch.idA) tA = t;
        else if (t.identifier === pinch.idB) tB = t;
      }
      if (tA !== null && tB !== null) {
        ev.preventDefault();
        const dist = Math.hypot(tB.clientX - tA.clientX, tB.clientY - tA.clientY);
        if (dist > 0) {
          const ratio = dist / pinch.startDist;
          const deltaDeg = (1 - ratio) * PINCH_FOV_GAIN;
          const target = pinch.startFovDeg + deltaDeg;
          const clamped = Math.max(fovMin, Math.min(fovMax, target));
          callbacks.onPinch({ fovDeg: clamped });
        }
        return;
      }
    }
    if (drag !== null) {
      // Scorri changedTouches: per il dito drag attivo, calcola delta.
      for (let i = 0; i < ev.changedTouches.length; i++) {
        const t = ev.changedTouches.item(i);
        if (t === null) continue;
        if (t.identifier !== drag.identifier) continue;
        ev.preventDefault();
        const dx = t.clientX - drag.lastX;
        const dy = t.clientY - drag.lastY;
        drag.lastX = t.clientX;
        drag.lastY = t.clientY;
        // Convenzione: drag destro → yaw negativo (camera ruota destra).
        // drag in basso → pitch negativo (camera guarda giu').
        // Codex iter2 add#2 (WARNING): emit raw delta. L'orchestrator
        // accumula su walkPitch globale e applica clampPitch su quello
        // (vedi installWalkTouchHandlers in orchestrator.ts). Senza
        // accumulo locale, la clamp e' simmetrica anche con drag multipli
        // a partire da walkPitch != 0.
        const deltaYaw = -dx * sensitivity;
        const deltaPitch = -dy * sensitivity;
        callbacks.onRotate({ deltaYaw, deltaPitch });
      }
    }
  };

  const onTouchEnd = (ev: TouchEvent): void => {
    for (let i = 0; i < ev.changedTouches.length; i++) {
      const t = ev.changedTouches.item(i);
      if (t === null) continue;
      if (pinch !== null && (t.identifier === pinch.idA || t.identifier === pinch.idB)) {
        // Pinch chiuso → torniamo a drag con il dito sopravvissuto, se c'e'.
        const survivorId = t.identifier === pinch.idA ? pinch.idB : pinch.idA;
        let survivor: Touch | null = null;
        for (let j = 0; j < ev.touches.length; j++) {
          const tt = ev.touches.item(j);
          if (tt !== null && tt.identifier === survivorId) {
            survivor = tt;
            break;
          }
        }
        pinch = null;
        if (survivor !== null) {
          drag = {
            identifier: survivor.identifier,
            lastX: survivor.clientX,
            lastY: survivor.clientY,
          };
        } else {
          drag = null;
        }
        continue;
      }
      if (drag !== null && t.identifier === drag.identifier) {
        drag = null;
      }
    }
  };

  canvas.addEventListener('touchstart', onTouchStart, { passive: false });
  canvas.addEventListener('touchmove', onTouchMove, { passive: false });
  canvas.addEventListener('touchend', onTouchEnd, { passive: false });
  canvas.addEventListener('touchcancel', onTouchEnd, { passive: false });

  return {
    dispose(): void {
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('touchend', onTouchEnd);
      canvas.removeEventListener('touchcancel', onTouchEnd);
      drag = null;
      pinch = null;
    },
  };
}
